import { OperationVariables, Unmasked } from '@apollo/client'
import { FetchMoreFunction } from '@apollo/client/react/hooks/useSuspenseQuery'
import { useCallback, useState } from 'react'

interface FetchMorePagination<R, T extends OperationVariables> {
  loading: boolean;
  fetchMore: FetchMoreFunction<R, OperationVariables>;
  page?: number;
  pageVariable?: string;
  usePager?: boolean;
  variables?: T;
  updaterFunction: (cached: R, fetched: R) => { hasMore: boolean; result: R };
}

export default function useFetchMorePagination<
  R,
  T extends OperationVariables,
> ({
  loading,
  fetchMore,
  page = 1,
  pageVariable = 'page',
  usePager = false,
  variables,
  updaterFunction,
}: FetchMorePagination<R, T>) {
  const [ offset, setOffset ] = useState(page)
  const [ hasMore, setHasMore ] = useState(true)
  const [ loadingMore, setLoadingMore ] = useState(false)

  const clearPagination = useCallback(() => {
    setOffset(1)
    setHasMore(true)
  }, [])

  const fetchNextPage = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      const newOffset = offset + 1
      setLoadingMore(true)

      fetchMore({
        variables: {
          ...variables,
          [pageVariable]: usePager
            ? {
              limit: variables?.[pageVariable]?.limit,
              page: newOffset,
            }
            : newOffset,
        },
        updateQuery (
          previousResult: Unmasked<R>,
          {
            fetchMoreResult,
          }: {
            fetchMoreResult: Unmasked<R>;
            variables: OperationVariables;
          },
        ): Unmasked<R> {
          const { hasMore = false, result } = updaterFunction(
            previousResult as R,
            fetchMoreResult as R,
          )
          setHasMore(hasMore)
          setOffset(newOffset)
          return result as Unmasked<R>
        },
      })
        .then(() => {
          setLoadingMore(false)
        })
        .catch(() => {
          setLoadingMore(false)
          setHasMore(false)
        })
    }
  }, [
    fetchMore,
    hasMore,
    loading,
    loadingMore,
    offset,
    pageVariable,
    updaterFunction,
    variables,
    usePager,
  ])

  return {
    currentPage: offset,
    hasMore,
    loadingMore,
    fetchNextPage,
    clearPagination,
  }
}
